/**
 * Deterministic speaker color utility
 * Hashes speaker name to a consistent hue for visual identification
 * Adapted from live-tranz/src/components/speaker/speakerColors.ts
 */

// 12 distinct, accessible hues with good light/dark mode contrast
const SPEAKER_HUES = [
  210, // blue
  150, // teal
  30,  // orange
  280, // purple
  350, // rose
  170, // cyan
  50,  // amber
  260, // violet
  190, // sky
  10,  // red
  120, // green
  40,  // yellow
]

/**
 * Simple string hash (djb2) → deterministic index
 */
function hashString(str: string): number {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0
  }
  return Math.abs(hash)
}

/**
 * Get a deterministic HSL color for a speaker name.
 * Returns a CSS `hsl()` string with fixed saturation and lightness.
 */
export function getSpeakerColor(name: string): string {
  if (!name) return 'hsl(210, 60%, 50%)'
  const idx = hashString(name) % SPEAKER_HUES.length
  const hue = SPEAKER_HUES[idx]
  return `hsl(${hue}, 60%, 50%)`
}

/**
 * Get a lighter variant for backgrounds / subtle highlights
 */
export function getSpeakerColorLight(name: string): string {
  if (!name) return 'hsl(210, 60%, 95%)'
  const idx = hashString(name) % SPEAKER_HUES.length
  const hue = SPEAKER_HUES[idx]
  return `hsl(${hue}, 60%, 95%)`
}

/**
 * Get a dark-mode variant for backgrounds / subtle highlights
 */
export function getSpeakerColorDark(name: string): string {
  if (!name) return 'hsl(210, 60%, 20%)'
  const idx = hashString(name) % SPEAKER_HUES.length
  const hue = SPEAKER_HUES[idx]
  return `hsl(${hue}, 60%, 20%)`
}

/**
 * Get the hue number for a speaker (useful for inline styles)
 */
export function getSpeakerHue(name: string): number {
  if (!name) return 210
  return SPEAKER_HUES[hashString(name) % SPEAKER_HUES.length]
}
